import { useElementSize } from "@/hooks/use-element-size";
import { useScreenSize } from "@/providers/ScreenSizeProvider";
import { ScrollElementContext } from "@/providers/scroll-element";
import { motion } from "framer-motion";
import { useContext, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Card } from "../Card";

type Props = {
  children: React.ReactNode;
  className?: string;
  direction?: "left" | "right";
};

const containerEase = [0.42, 0, 0.58, 1] as const;
const ease = [0.645, 0.045, 0.355, 1] as const;

export function Sidebar({ children, direction = "right", className }: Props) {
  const scrollEl = useContext(ScrollElementContext);
  const { 1: scrollElHeight } = useElementSize(scrollEl);
  const { isXLBreakpoint } = useScreenSize();

  const [cardWrapperEl, setCardWrapperEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!scrollEl || !cardWrapperEl) return;

    const onScroll = () => {
      cardWrapperEl.style.setProperty(
        "--scroll-top",
        `${scrollEl.scrollTop}px`,
      );
    };

    onScroll();

    scrollEl.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      scrollEl.removeEventListener("scroll", onScroll);
    };
  }, [scrollEl, cardWrapperEl]);

  // The values come from the padding defined in the __root.tsx file for the scrollable container
  const outerPadding = useMemo(
    () => (isXLBreakpoint ? 28 : 14),
    [isXLBreakpoint],
  );

  // This defines the max height of the sidebar which is calculated based on the scrollable container height minus (the padding * 2)
  const maxCardHeight = useMemo(
    () => scrollElHeight - outerPadding * 2,
    [scrollElHeight, outerPadding],
  );
  // Defines the min height of the sidebar which is max height - (height of topbar + spacing between topbar and sidebar)
  const minCardHeight = useMemo(() => maxCardHeight - 48, [maxCardHeight]);

  useEffect(() => {
    if (!cardWrapperEl) return;

    const el = cardWrapperEl;
    el.style.setProperty("--sidebar-min-card-height", `${minCardHeight}px`);
    el.style.setProperty("--sidebar-max-card-height", `${maxCardHeight}px`);
  }, [cardWrapperEl, minCardHeight, maxCardHeight]);

  return (
    <motion.div
      className={twMerge("flex max-w-60 min-w-0 xl:max-w-80", className)}
      initial={{ width: 0 }}
      animate={{
        width: "100%",
        transition: { duration: 0.35, ease: containerEase },
      }}
      exit={{
        width: 0,
        transition: {
          duration: 0.35,
          delay: 0.2,
          ease: containerEase,
        },
      }}
    >
      <motion.div
        ref={setCardWrapperEl}
        className="sticky h-full w-full"
        initial={{ x: direction === "left" ? -80 : 80, opacity: 0 }}
        animate={{
          x: 0,
          opacity: 1,
          transition: { delay: 0.2, ease, duration: 0.4 },
        }}
        exit={{
          x: direction === "left" ? -80 : 80,
          opacity: 0,
          transition: { duration: 1, ease },
        }}
        style={
          {
            top: outerPadding,
            "--sidebar-min-card-height": `${minCardHeight}px`,
            "--sidebar-max-card-height": `${maxCardHeight}px`,
            "--scroll-top": `${scrollEl?.scrollTop || 0}px`,
            maxHeight: `
                  clamp(
                    var(--sidebar-min-card-height),
                    calc(var(--sidebar-min-card-height) + var(--scroll-top)),
                    var(--sidebar-max-card-height)
                  )
                    `,
            transition: "max-height 0.1s ease 0.15s",
          } as React.CSSProperties
        }
      >
        <Card className="size-full shrink-0 grow-0 pb-4 xl:pb-8">
          {children}
        </Card>
      </motion.div>
    </motion.div>
  );
}

export function SidebarContent({ children, className }: Props) {
  return (
    <motion.aside
      className={twMerge("h-full w-full", className)}
      initial={{ x: -16, opacity: 0 }}
      exit={{ x: 16, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      {children}
    </motion.aside>
  );
}
